import { useMemo } from 'react';
import { useDrag } from '@use-gesture/react';
import { useSpring } from '@react-spring/web';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';

interface Params {
	onDismiss?: () => void;
}

/**
 * Returns the necessary props to create a swipe gesture, which should be passed
 * to an `animated` component from `react-spring`.
 */
export function useSwipeToDismiss({ onDismiss }: Params) {
	const isMobileScreenSize = useIsMobileScreenSize();

	// `api` orchestrates the animation, `x` & `y` get passed to the animated element.
	const [{ x, y }, api] = useSpring(() => ({ x: 0, y: 0 }));

	const bind = useDrag(
		({ last, down, movement: [mx] }) => {
			// If the user has swiped enough left, count it as a dismissal.
			if (last && mx < -60) {
				onDismiss?.();
				return;
			}

			// Otherwise, update the position of the element if the user is pressing
			// down and has moved left.
			const x = down && mx < 0 ? mx : 0;
			api.start({ x, immediate: down });
		},
		{
			// Only allow swiping on the x axis.
			axis: 'x',
			// Only allow swiping left.
			bounds: { right: 0 },
			// Necessary for swiping to work on Android.
			pointer: { touch: true },
		}
	);

	return useMemo(() => {
		if (!isMobileScreenSize) {
			return;
		}

		return {
			style: {
				x,
				y,
				// Prevent the user's swipe from also triggering a scroll.
				touchAction: 'none',
			},
			...bind(),
		};
	}, [isMobileScreenSize, x, y, bind]);
}
